import { Users, UserCog, Heart, Calendar, TrendingUp, Star, UserPlus, Plus, Megaphone, Receipt, Star as StarIcon, ClipboardCheck, ChevronRight } from 'lucide-react';
import { StatsStrip } from '@/components/director/stats-strip';
import { useDirectorStats } from '@/hooks/use-director-data';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const activityIcons = {
  evaluation: StarIcon,
  invoice_paid: Receipt,
  session: ClipboardCheck,
  player_joined: Users,
};

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function DirectorDashboard() {
  const { data: stats, isLoading: statsLoading } = useDirectorStats();
  const navigate = useNavigate();

  const activity = useQuery(api.director.recentActivity.list);

  const stripStats = [
    { icon: Users, value: stats?.totalPlayers ?? 0, label: 'Players', color: 'blue' as const },
    { icon: UserCog, value: stats?.totalCoaches ?? 0, label: 'Coaches', color: 'purple' as const },
    { icon: Heart, value: stats?.totalParents ?? 0, label: 'Parents', color: 'green' as const },
    { icon: Calendar, value: stats?.sessionsThisWeek ?? 0, label: 'Sessions This Week', color: 'orange' as const },
    { icon: TrendingUp, value: `${stats?.avgAttendance ?? 0}%`, label: 'Avg Attendance', color: 'blue' as const },
    { icon: Star, value: stats?.recentEvaluations ?? 0, label: 'Recent Evals', color: 'purple' as const },
  ];

  const quickActions = [
    { label: 'Add Player', icon: Plus, href: '/director/players' },
    { label: 'New Invoice', icon: Receipt, href: '/director/invoices' },
    { label: 'Announcement', icon: Megaphone, href: '/director/announcements' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats strip — compact horizontal scroll */}
      <StatsStrip stats={stripStats} isLoading={statsLoading} />

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-3">
            {quickActions.map(({ label, icon: Icon, href }) => (
              <button
                key={label}
                onClick={() => navigate(href)}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-surface-raised hover:bg-secondary transition-colors text-center group"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-xs font-medium text-foreground leading-tight">{label}</span>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Recent Activity</CardTitle>
            <Link to="/director/analytics" className="text-xs text-primary hover:underline">
              View all
            </Link>
          </CardHeader>
          <CardContent className="space-y-1">
            {activity === undefined ? (
              // Loading
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="w-8 h-8 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-3 w-3/4" />
                      <Skeleton className="h-3 w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !activity || activity.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <ClipboardCheck className="w-8 h-8 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No recent activity yet</p>
              </div>
            ) : (
              activity.map((item, i) => {
                const Icon = activityIcons[item.type as keyof typeof activityIcons] ?? Star;
                return (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <div className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                      item.type === 'evaluation' && 'bg-violet-50 text-violet-600 dark:bg-violet-950/40',
                      item.type === 'invoice_paid' && 'bg-success/10 text-success',
                      item.type === 'session' && 'bg-info/10 text-info',
                      item.type === 'player_joined' && 'bg-orange-50 text-orange-500 dark:bg-orange-950/40',
                    )}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">{item.description}</p>
                      <p className="text-xs text-muted-foreground">{timeAgo(item.timestamp)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      {/* Empty state when no players yet */}
      {!statsLoading && (stats?.totalPlayers ?? 0) === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <UserPlus className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-foreground font-medium mb-1">No players yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Add players to start tracking sessions, attendance, and progress.
            </p>
            <Link to="/director/players" className="text-sm text-primary hover:underline">
              Go to Players →
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
